import { useMemo } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import { useStableQuery } from './useStableQuery';
import type { MangoQuery } from 'rxdb';
import {
    entityNameOfCollection,
    usePersonLabels
} from '../features/users/personLabels';

/**
 * Every row of a collection that belongs to ONE project, as plain JSON.
 *
 * The same five lines stood at thirty-odd call sites, differing only in the
 * collection name — and not always agreeing: some asked for plain objects and
 * some forgot, which is the difference between an object and an RxDocument
 * further down. Here it is decided once: JSON, always.
 *
 * `extraSelector` narrows the query beyond the project; it must be a stable
 * reference — a module-level constant, not an inline object. RxDB's hook
 * subscribes anew whenever the query OBJECT changes identity, so an inline one
 * would resubscribe on every render.
 */
export function useProjectDocs<T extends { projectId: string }>(
    collection: string,
    projectId: string,
    extraSelector?: Record<string, unknown>
) {
    const query = useStableQuery<T>({
        selector: { projectId, ...extraSelector }
    } as MangoQuery<T>);
    return useJsonQuery<T>(collection, query);
}

/**
 * Every row of a collection across a whole Teilmaßnahme family, as plain JSON.
 *
 * Takes the ids rather than the project so a screen loading several collections
 * resolves the family ONCE (`useFamilyProjectIds`) and spends one subscription
 * on it instead of one per collection.
 *
 * The family is the right scope wherever a Befund is involved: linked projects
 * share their Befunde, so a row referencing one may sit in any phase.
 */
export function useFamilyDocs<T extends { projectId: string }>(
    collection: string,
    familyIds: string[],
    extraSelector?: Record<string, unknown>
) {
    // By value, not by identity: `familyIds` is a fresh array whenever the
    // projects query emits, and rebuilding the query object there would
    // resubscribe for nothing (see useStableQuery).
    const query = useStableQuery<T>({
        selector: {
            projectId: { $in: familyIds },
            ...extraSelector
        }
    } as MangoQuery<T>);
    return useJsonQuery<T>(collection, query);
}

/**
 * Any live query as plain JSON, for the scopes the two above do not cover (every
 * project, a selector that depends on the screen). The query may be written
 * inline: it is held steady by value (see useStableQuery).
 */
export function useLiveDocs<T>(collection: string, query: MangoQuery<T> = {}) {
    return useJsonQuery<T>(collection, useStableQuery<T>(query));
}

/**
 * The live query all of them run: RxDB's own hook, with its RxDocuments turned
 * into plain objects once per result rather than at every reader — and every
 * linked person shown by their account (usePersonLabels), so no reader has to
 * remember to.
 */
function useJsonQuery<T>(collection: string, query: MangoQuery<T>) {
    const { results, loading } = useLiveRxQuery<T>({ collection, query });
    const personLabels = usePersonLabels();
    const rows = useMemo(() => {
        const entityName = entityNameOfCollection(collection);
        return results.map((doc) =>
            personLabels.show(entityName, doc.toMutableJSON() as T & object)
        ) as T[];
    }, [results, personLabels, collection]);
    return { results: rows, loading };
}
